import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    PlayArrow as PlayIcon,
    AccountTree as WorkflowIcon,
    Schedule as ScheduleIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { Workflow, WorkflowExecution, ExecutionStatus } from '../../types';

export default function Dashboard() {
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
    const [loadingWorkflows, setLoadingWorkflows] = useState(true);
    const [loadingExecutions, setLoadingExecutions] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchWorkflows();
        fetchRecentExecutions();
    }, []);

    const fetchWorkflows = async () => {
        try {
            setLoadingWorkflows(true);
            
            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }
            
            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/workflows`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Handle both array response and object with items property
            let workflowsData: any[] = [];

            if (Array.isArray(data)) {
                workflowsData = data;
            } else if (typeof data === 'object' && data !== null && 'items' in data && Array.isArray(data.items)) {
                workflowsData = data.items;
            } else {
                throw new Error('Invalid API response format');
            }

            setWorkflows(workflowsData);
        } catch (error) {
            console.error('Error fetching workflows:', error);
            setError('Failed to load workflows data');
        } finally {
            setLoadingWorkflows(false);
        }
    };

    const fetchRecentExecutions = async () => {
        try {
            setLoadingExecutions(true);
            
            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                setError('Authentication error. Please sign in again.');
                return;
            }
            
            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/executions?limit=5`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Handle both array response and object with items property
            let executionsData: any[] = [];

            if (Array.isArray(data)) {
                executionsData = data;
            } else if (typeof data === 'object' && data !== null && 'items' in data && Array.isArray(data.items)) {
                executionsData = data.items;
            } else {
                throw new Error('Invalid API response format');
            }

            setExecutions(executionsData);
        } catch (error) {
            console.error('Error fetching executions:', error);
            setError('Failed to load recent executions');
        } finally {
            setLoadingExecutions(false);
        }
    };

    // Calculate stats based on real data
    const totalWorkflows = workflows.length;
    const activeWorkflows = workflows.filter(w => w.enabled || w.isActive).length;
    const scheduledWorkflows = workflows.filter(w => w.schedule && w.schedule.trim() !== '').length;

    const stats = [
        {
            title: 'Total Workflows',
            value: totalWorkflows,
            icon: <WorkflowIcon fontSize="large" color="primary" />,
        },
        {
            title: 'Active Workflows',
            value: activeWorkflows,
            icon: <PlayIcon fontSize="large" color="success" />,
        },
        {
            title: 'Scheduled Workflows',
            value: scheduledWorkflows,
            icon: <ScheduleIcon fontSize="large" color="info" />,
        },
    ];

    // Format date to relative time (e.g., "2 hours ago")
    const formatRelativeTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMilliseconds = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInDays > 0) {
            return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
        } else if (diffInHours > 0) {
            return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        } else if (diffInMinutes > 0) {
            return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        } else {
            return 'Just now';
        }
    };

    // Function to get workflow name by ID
    const getWorkflowName = (workflowId: string): string => {
        const workflow = workflows.find(w => w.id === workflowId);
        return workflow ? workflow.name : 'Unknown Workflow';
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    Dashboard
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/workflows/new')}
                >
                    Create Workflow
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                {stats.map((stat) => (
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 calc(33.33% - 16px)' } }} key={stat.title}>
                        <Card>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography color="textSecondary" gutterBottom>
                                            {stat.title}
                                        </Typography>
                                        {loadingWorkflows ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <Typography variant="h4">{stat.value}</Typography>
                                        )}
                                    </Box>
                                    {stat.icon}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                ))}
            </Box>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Recent Executions
                        </Typography>
                        <Button
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate('/executions')}
                        >
                            View All
                        </Button>
                    </Box>

                    {loadingExecutions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : executions.length === 0 ? (
                        <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                            No workflow executions yet
                        </Typography>
                    ) : (
                        <List>
                            {executions.map((execution) => (
                                <ListItem
                                    key={execution.id}
                                    disablePadding
                                    divider
                                >
                                    <ListItemButton
                                        onClick={() => navigate(`/executions/${execution.id}`)}
                                    >
                                        <ListItemText
                                            primary={getWorkflowName(execution.workflowId)}
                                            secondary={
                                                execution.startedAt ?
                                                    formatRelativeTime(execution.startedAt) :
                                                    'No start time'
                                            }
                                        />
                                    </ListItemButton>
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" size="small">
                                            {execution.status === ExecutionStatus.COMPLETED ? (
                                                <SuccessIcon color="success" />
                                            ) : execution.status === ExecutionStatus.FAILED ? (
                                                <ErrorIcon color="error" />
                                            ) : (
                                                <PlayIcon color="info" />
                                            )}
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
